import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import Icon from '../components/Icon';
import Menu from '../components/Menu';
import Note from '../components/Note';
import Sheet, { SETTLE_NOTE } from '../components/Sheet';
import DayArc from '../components/DayArc';
import DeviceHealth from '../components/DeviceHealth';
import DeviceName from '../components/DeviceName';
import SettingLocation from '../components/SettingLocation';
import SettingPrayerCalc from '../components/SettingPrayerCalc';
import SettingAzan from '../components/SettingAzan';
import SettingQuran from '../components/SettingQuran';
import SettingBkImages from '../components/SettingBkImages';
import SettingDisplayTheme from '../components/SettingDisplayTheme';
import DeviceShare from '../components/DeviceShare';

import { usePrayerTimes, formatRemaining, minutesOfDay, minutesNowIn } from '../hooks/usePrayerTimes';
import { useDeviceHealth } from '../hooks/useDeviceHealth';
import { useOptions } from '../hooks/useOptions';
import { useTheme } from '../hooks/useTheme';
import { useBackToClose } from '../hooks/useBackToClose';
import { useInstallApp } from '../hooks/useInstallApp';
import { prayerGlyph, prayerArabic } from '../prayers';
import { getData, postData, postJson } from '../global/func';
import logo from '../assets/images/logo-mark.png';

// Louder the closer it gets, on the same thresholds the television uses. The
// point of the pill is that it can be read without reading it: the colour says
// how soon before the number does.
const tierFor = (minutes) =>
  minutes <= 10 ? 'now'
    : minutes <= 30 ? 'soon'
      : minutes <= 60 ? 'near'
        : 'later';

// The Hijri month names, written out rather than asked for.
//
// month:'long' under the islamic calendar looked right in Node and produced
// "April 1" on an Android phone: that device has no Islamic month names for the
// locale, so ICU fell back to the *Gregorian* name list and indexed it by the
// Hijri month number -- Rabi' II is the fourth Hijri month, so it printed
// April. A date that is confidently wrong is worse than one that is missing,
// and this is not something to leave to whatever data a phone happens to ship.
//
// The numbers still come from Intl, which is what actually knows the
// conversion. Only the naming is ours.
const HIJRI_MONTHS = [
  'Muharram', 'Safar', "Rabi' al-Awwal", "Rabi' al-Thani",
  'Jumada al-Ula', 'Jumada al-Akhirah', 'Rajab', "Sha'ban",
  'Ramadan', 'Shawwal', "Dhu al-Qi'dah", 'Dhu al-Hijjah',
];

const hijriToday = (now) => {
  try {
    const fmt = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', {
      day: 'numeric', month: 'numeric',
    });

    // If the engine ignored the calendar request it would hand back a Gregorian
    // date wearing a Hijri label, which is the failure this is avoiding.
    if (!String(fmt.resolvedOptions().calendar).startsWith('islamic')) return '';

    const parts = fmt.formatToParts(now);
    const day = parts.find((p) => p.type === 'day')?.value;
    const month = Number(parts.find((p) => p.type === 'month')?.value);

    if (!day || !Number.isFinite(month) || month < 1 || month > 12) return '';
    return `${day} ${HIJRI_MONTHS[month - 1]}`;
  } catch {
    return '';
  }
};

// Both calendars, because both are wanted and neither is guessable from the
// other. A missing Hijri date is not worth a blank line, so it simply drops.
const dateLine = () => {
  const now = new Date();
  let greg = '';
  try {
    greg = new Intl.DateTimeFormat('en', { day: 'numeric', month: 'long' }).format(now);
  } catch { /* nothing sensible to fall back to */ }

  const hijri = hijriToday(now);
  return [hijri, greg].filter(Boolean).join(' · ');
};

// Which box the customer was last looking at.
//
// Held as the device key rather than the position in the list, because the list
// is rebuilt from the server on every sign-in and after any change to it: claim
// a second box, or have a shared one taken away, and index 1 is a different
// box than it was. A key either still names a box in the list or it does not.
const ACTIVE_DEVICE_KEY = 'app_active_device';

const savedDeviceIndex = (devices) => {
  try {
    const key = localStorage.getItem(ACTIVE_DEVICE_KEY);
    if (!key) return 0;
    const found = devices.findIndex((d) => d.device_key === key);
    // Gone -- unshared, or signed in as somebody else. The first box is the
    // honest answer; remembering a box they can no longer see is not.
    return found >= 0 ? found : 0;
  } catch { return 0; }
};

const Home = ({ User, Devices, Logout, onAddDevice, onDevicesChanged }) => {
  const [activeDevice, setActiveDevice] = useState(() => savedDeviceIndex(Devices));
  const [settings, setSettings] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [sheet, setSheet] = useState(null);          // 'times' | 'sound' | 'screen' | 'device'
  const [menu, setMenu] = useState(null);            // 'account' | 'devices'
  const [busyPlaying, setBusyPlaying] = useState(false);

  const device = Devices[activeDevice];
  const deviceKey = device?.device_key;

  // Remembered here rather than in the click handler, so what gets saved is
  // whatever the customer is actually looking at -- including the fallback the
  // clamp below may have just moved them to.
  useEffect(() => {
    if (!deviceKey) return;
    try {
      localStorage.setItem(ACTIVE_DEVICE_KEY, deviceKey);
    } catch { /* the choice still holds for this visit */ }
  }, [deviceKey]);

  // The list can shrink underneath the selection: a shared box withdrawn while
  // the app is open arrives on the next onDevicesChanged. An index past the end
  // leaves `device` undefined, which the settings load below reads a key off.
  useEffect(() => {
    if (activeDevice >= Devices.length) setActiveDevice(0);
  }, [Devices, activeDevice]);

  // The phone's Back button, which without this closes the app from an open
  // sheet. One flag covers the sheets and the two menus together -- see the
  // hook for why they cannot each have their own.
  const anyOverlay = sheet !== null || menu !== null;
  const closeOverlays = useCallback(() => { setSheet(null); setMenu(null); }, []);
  useBackToClose(anyOverlay, closeOverlays);

  // Offered in the account menu as well as in the banner -- see the item itself
  // for why. iOS can always be sent back to the instructions; everyone else
  // needs a captured prompt to have something to show.
  const { canPrompt, installed, needsShare, promptInstall, undismiss } = useInstallApp();
  const canOfferInstall = !installed && (canPrompt || needsShare);


  // owner_email is only present in an administrator's response, so its presence
  // is what says which of the two lists this is. An owner sees the name they
  // chose; an administrator sees that name and needs the serial and the owner
  // beside it, because "Living room" on a fleet list is worse than a serial
  // number.
  const isAdmin = Devices.some((d) => d.owner_email);

  const titleOf = (d) => d?.name || (isAdmin ? d?.serial_no : 'Prayer Box');
  const subtitleOf = (d) => (isAdmin ? [d?.owner_email, d?.serial_no].filter(Boolean).join(' · ') : '');

  // ----------------------------------------------------------- the settings

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoadError('');
      // Undefined for the one render between a box leaving the list and the
      // clamp above moving off it. There is nothing to load for a box that is
      // not there, and the clamp re-runs this with a good key.
      if (!deviceKey) return;
      const auth = {
        DEVICE_KEY: deviceKey,
        SESSION_ID: import.meta.env.REACT_APP_SESSION_ID,
      };
      const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/settings/');
      const loaded = await getData(url, auth);

      if (cancelled) return;

      // A failed request used to throw on loaded[0]. The rejection went
      // unhandled, the state stayed undefined, and the page sat on "Loading"
      // with no way out -- not even sign-out, which lives in the tree that never
      // rendered.
      if (!Array.isArray(loaded) || loaded.length === 0) {
        setLoadError('Could not load the settings for this box.');
        return;
      }
      setSettings({ ...loaded[0] });
    })();

    return () => { cancelled = true; };
  }, [deviceKey, reloadKey]);

  // Awaited, and that is the whole point.
  //
  // The save used to be fired and forgotten while the state updated on the next
  // line. The prayer times refetch the moment that state changes, and they ask
  // the *server* -- so the two raced, the refetch usually won, and it came back
  // describing the settings as they were before the change. That is what made
  // switching the time source look like it had not taken.
  const onSettingChange = useCallback(async (changed) => {
    const auth = {
      DEVICE_KEY: Devices[activeDevice].device_key,
      SESSION_ID: import.meta.env.REACT_APP_SESSION_ID,
    };
    const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/settings/update.php');

    const saved = await postData(url, changed[0], auth);
    if (saved === undefined) {
      setSaveError('That change could not be saved. Check your connection and try again.');
      return false;
    }
    setSaveError('');
    setSettings(changed[0]);
    return true;
  }, [Devices, activeDevice]);

  // ------------------------------------------------------- the prayer times

  const { preview, upcoming, loading, error, refresh } = usePrayerTimes(deviceKey, settings);
  const { describe: reciterName } = useOptions(deviceKey, 'reciter_list');
  const { theme, choose: chooseTheme } = useTheme();

  // "quran" is a stored setting rather than a live transport -- see the toggle
  // below. Read here because how often the box is worth asking about depends on
  // it: the recitation moves every few seconds, everything else on that health
  // reply moves every fifteen minutes.
  const playing = settings?.['quran'] === 'true';
  const { health, role: liveRole } = useDeviceHealth(deviceKey, User, playing ? 15 * 1000 : 60 * 1000);

  // Owner or member of a shared box. The server decides this and refuses a
  // member's write regardless of what is on screen; this is what stops the app
  // offering controls that would then fail, which is a worse way to learn.
  //
  // The live answer wins over the one in the device list. That list is written
  // at sign-in and kept in local storage, so somebody who signed in before
  // sharing existed holds a list with no role on it -- which read as "owner" and
  // showed a member the box tab, sharing panel and all.
  //
  // Absent from both still means owner, because that is what everyone was before
  // this existed; the live role arrives within a second and corrects it.
  const isMember = (liveRole || device?.role) === 'member';

  // Where the box's recitation is up to: playing it now, or waiting to resume
  // from it. Shown either way, and never aged out.
  //
  // It used to be shown only while playing and only if reported in the last
  // forty seconds, which made it flicker -- the box reports every fifteen
  // seconds and the app asks every fifteen, so the two drift in and out of that
  // window. Worse, it was missing at the one moment it is most worth having:
  // before pressing play, when what it names is what play is about to recite.
  const atAya = health?.now_playing || null;

  // ---------------------------------------------------------- recitation

  // Heard daily, chosen once -- so only the daily half is here. Which reciter,
  // and whether recitation is allowed at all, stay in Sound.
  //
  // "quran" is a stored setting rather than a live transport, so starting and
  // stopping means saving it and then asking the display to pick it up. The
  // reload is what makes it immediate: without it the change waits for the box
  // to refresh on its own. `playing` itself is read further up, where the health
  // poll needs it.

  const toggleRecitation = async () => {
    if (!settings || busyPlaying) return;
    setBusyPlaying(true);

    const next = [{ ...settings, quran: playing ? 'false' : 'true' }];
    const ok = await onSettingChange(next);

    if (ok) {
      // Owners may send this one; it is how the television is told to look
      // again. A failure here is not worth an error -- the setting is saved and
      // the box will catch up on its own.
      try {
        const url = encodeURI(import.meta.env.REACT_APP_API_SERVER_URL + '/device/command/');
        await postJson(url, {
          command: 'reload-page',
          DEVICE_KEY: deviceKey,
          USER_ID: User.id,
          AUTH_KEY: User.auth_key,
        });
      } catch { /* the setting is what matters */ }
    }
    setBusyPlaying(false);
  };

  // ------------------------------------------------------------------ render

  if (!settings) {
    return (
      <div className="column">
        <div className="appbar">
          <img className="appbar__logo" src={logo} alt="" />
          <div className="appbar__who">
            <div className="appbar__name">{loadError ? 'Unavailable' : 'Prayer Box'}</div>
            <div className="appbar__where">{loadError ? 'Something went wrong' : 'Loading…'}</div>
          </div>
        </div>
        {loadError ? (
          <>
            <div className="notice notice--warn">{loadError}</div>
            <p className="sheet__sub">
              This usually means the box is not reachable, or its settings have not been created yet.
            </p>
            <div className="actions">
              <button className="btn btn--primary" onClick={() => setReloadKey((k) => k + 1)}>
                <Icon name="refresh" size={16} /> Try again
              </button>
              <button className="btn" onClick={Logout}>Sign out</button>
            </div>
          </>
        ) : (
          <div className="centered"><span className="spinner" /></div>
        )}
      </div>
    );
  }

  const prayers = preview?.prayers ?? [];
  const tz = preview?.location?.tz;
  const now = tz ? minutesNowIn(tz) : 0;

  // A box checks in every fifteen minutes when idle, so silence shorter than
  // that is normal and should not be dressed up as a fault. The age comes from
  // the server already measured -- see useDeviceHealth for why it cannot be
  // worked out here.
  const status = (() => {
    if (!health) return { cls: 'dot--off', text: 'checking…' };
    if (health.last_mode === 'locked') return { cls: 'dot--warn', text: 'needs attention' };

    const age = Number(health.checkin_age_seconds);
    if (!Number.isFinite(age)) return { cls: 'dot--off', text: 'never reported' };
    if (age < 45 * 60) return { cls: 'dot--ok', text: 'online' };
    if (age < 24 * 3600) return { cls: 'dot--off', text: `last seen ${Math.round(age / 60)} min ago` };
    return { cls: 'dot--off', text: 'not reporting' };
  })();

  const place = preview ? `${preview.location.city}` : '';

  return (
    <div className="column">

      {/* ------------------------------------------------------------ appbar */}
      <div className="appbar">
        <img className="appbar__logo" src={logo} alt="Prayer Box" />
        <div className="appbar__who">
          {Devices.length > 1 ? (
            <button
              className="appbar__name appbar__name--switch"
              onClick={(e) => { e.stopPropagation(); setMenu(menu === 'devices' ? null : 'devices'); }}
              aria-haspopup="true" aria-expanded={menu === 'devices'}
            >
              {titleOf(device)}
              <Icon name="chevron" size={14} className="rot90" />
            </button>
          ) : (
            <div className="appbar__name">{titleOf(device)}</div>
          )}
          <div className="appbar__where">
            <span className={'dot ' + status.cls} />
            {place ? `${place} · ${status.text}` : status.text}
          </div>
        </div>
        <button
          className="icon-btn"
          onClick={(e) => { e.stopPropagation(); setMenu(menu === 'account' ? null : 'account'); }}
          aria-haspopup="true" aria-expanded={menu === 'account'} aria-label="Account and settings"
        >
          <Icon name="dots" size={18} />
        </button>
      </div>

      {menu === 'devices' && (
        <Menu align="left" label="Your boxes" onClose={() => setMenu(null)}>
          {Devices.map((d, i) => (
            <button
              key={d.id}
              className={'menu__item' + (i === activeDevice ? ' menu__item--on' : '')}
              onClick={() => { setActiveDevice(i); setSettings(null); setMenu(null); }}
            >
              <Icon name="box" size={16} />
              <span className="menu__stack">
                {titleOf(d)}
                {subtitleOf(d) && <small>{subtitleOf(d)}</small>}
              </span>
              {i === activeDevice && <Icon name="check" size={14} className="menu__meta" />}
            </button>
          ))}
        </Menu>
      )}

      {menu === 'account' && (
        <Menu label="Account" onClose={() => setMenu(null)}>
          <div className="menu__head">
            <div className="menu__name">{User.name}</div>
            <div className="menu__email">{User.email || ''}</div>
          </div>
          {onAddDevice && (
            <button className="menu__item" onClick={() => { setMenu(null); onAddDevice(); }}>
              <Icon name="plus" size={16} /> Add a Prayer Box
            </button>
          )}
          {/* Open to a member too. What is inside it differs -- they see how the
              box is doing and who it is shared with, and neither the rename nor
              the buttons that command it. */}
          <button className="menu__item" onClick={() => { setMenu(null); setSheet('device'); }}>
            <Icon name="cog" size={16} /> The box
          </button>

          <div className="menu__sep" />
          <div className="menu__label">Appearance</div>
          {[
            // "My phone", not "my device". Everything else this app calls a
            // device is the box, so "Follow my device" read as "match the
            // television" -- a different promise entirely, and one this does
            // not keep.
            { id: '', label: 'Follow my phone' },
            { id: 'light', label: 'Light' },
            { id: 'dark', label: 'Dark' },
          ].map((t) => (
            <button
              key={t.id || 'system'}
              className={'menu__item' + (theme === t.id ? ' menu__item--on' : '')}
              onClick={() => chooseTheme(t.id)}
            >
              <Icon name={t.id === 'dark' ? 'moon' : t.id === 'light' ? 'sun' : 'phone'} size={16} />
              {t.label}
              {theme === t.id && <Icon name="check" size={14} className="menu__meta" />}
            </button>
          ))}

          {/* The banner is one tap from being gone for good, and the tap that
              does it is next to the one that installs. This is the way back:
              the same offer, somewhere it cannot be dismissed from.

              Hidden once the app is installed, and on a browser that has not
              said it could install it -- an item that cannot do anything is
              worse than no item. On iOS there is nothing to call, so it brings
              the banner back instead, which is where the Share instructions
              are. */}
          {canOfferInstall && (
            <>
              <div className="menu__sep" />
              <button
                className="menu__item"
                onClick={() => { setMenu(null); if (canPrompt) promptInstall(); else undismiss(); }}
              >
                <Icon name="download" size={16} /> Install the app
              </button>
            </>
          )}

          <div className="menu__sep" />
          <button className="menu__item menu__item--warn" onClick={Logout}>
            <Icon name="signout" size={16} /> Sign out
          </button>
        </Menu>
      )}

      {saveError && <div className="notice notice--warn">{saveError}</div>}

      {/* -------------------------------------------------------------- hero */}
      <div className="hero">
        <div className="hero__label">Next prayer</div>

        {upcoming ? (
          <>
            <div className="hero__prayer">
              {upcoming.prayer}
              <span className="hero__ar" lang="ar" dir="rtl">{prayerArabic(upcoming.prayer)}</span>
            </div>
            <div className="hero__at">
              at {prayers.find((p) => p.prayer === upcoming.prayer)?.[upcoming.tomorrow ? 'tomorrow' : 'today']}
              {upcoming.tomorrow ? ' tomorrow' : ''}
            </div>
            <div className="hero__count">
              {formatRemaining(upcoming.remaining)} <small>from now</small>
            </div>
          </>
        ) : (
          <div className="hero__prayer" style={{ fontSize: '1.4rem' }}>
            {loading ? 'Loading…' : error ? 'Times unavailable' : '—'}
          </div>
        )}

        {preview?.weather && (
          <div className="hero__weather">
            <img src={preview.weather.icon} alt="" />
            <span>
              {preview.weather.temperature}°C · feels like {preview.weather.feelsLike}°C · {preview.weather.description}
            </span>
          </div>
        )}

        {prayers.length > 0 && (
          <DayArc prayers={prayers} tz={tz} nextName={upcoming?.prayer} />
        )}
      </div>

      {/* ---------------------------------------------------- today's times */}
      <div className="section">
        <div className="section__head">
          <h2>Today</h2>
          <span className="section__meta">
            {dateLine()}
            <button className="icon-btn icon-btn--plain" onClick={refresh} disabled={loading}
                    title="Refresh the prayer times" aria-label="Refresh the prayer times">
              {loading ? <span className="spinner" /> : <Icon name="refresh" size={15} />}
            </button>
          </span>
        </div>

        {error && <div className="notice notice--warn">{error}</div>}

        {prayers.length > 0 ? (
          <div className="rows">
            {prayers.map((p) => {
              const isNext = upcoming?.prayer === p.prayer;
              const past = p.time24 && minutesOfDay(p.time24) < now && !isNext;
              return (
                <div key={p.prayer}
                     className={'row' + (isNext ? ' row--next' : past ? ' row--past' : '')}>
                  {/* On its own line above the prayer, not beside it. Inline, it
                      sat on top of the Arabic name and pushed the time on to a
                      second line -- the row is four columns on a narrow phone
                      and there is no room for a fifth thing in the middle one. */}
                  {isNext && (
                    <span className={'row__countdown row__countdown--' + tierFor(upcoming.remaining)}>
                      in {formatRemaining(upcoming.remaining)}
                    </span>
                  )}
                  <span className="row__glyph">{prayerGlyph(p.prayer)}</span>
                  <span className="row__name">
                    <span className="row__en">{p.prayer}</span>
                    {/* lang and dir so a screen reader switches voice, and so
                        the bidi algorithm is told rather than left to guess. */}
                    <span className="row__ar" lang="ar" dir="rtl">{prayerArabic(p.prayer)}</span>
                  </span>
                  <span className="row__time">{p.today}</span>
                  <span className="row__tomorrow">{p.tomorrow}</span>
                </div>
              );
            })}
          </div>
        ) : !loading && !error ? (
          <div className="centered">No prayer times for this location yet.</div>
        ) : null}
      </div>

      {/* -------------------------------------------------------- recitation */}
      <div className={'now-playing' + (playing ? ' now-playing--on' : '')}>
        <button
          className={'play-btn' + (playing ? ' play-btn--on' : '')}
          onClick={toggleRecitation}
          disabled={busyPlaying}
          aria-label={playing ? 'Stop the recitation' : 'Play the recitation'}
        >
          {busyPlaying ? <span className="spinner" /> : <Icon name={playing ? 'stop' : 'play'} size={16} />}
        </button>
        <div className="now-playing__meta">
          {/* When the box is telling us where it has got to, that is the line
              worth reading -- the reciter was chosen once and does not change,
              and it keeps its place underneath. */}
          <div className="now-playing__t1">
            {atAya
              ? <>
                  {/* The sura name comes out of the Quran table in Arabic --
                      the same string the television prints in its corner, which
                      is the point of this. It needs lang and dir of its own: it
                      sits beside a Latin word and a number, and without them the
                      browser resolves the run from the paragraph direction and
                      puts the number on the wrong side of the name. */}
                  <span className="now-playing__sura" lang="ar" dir="rtl">{atAya.sura_name}</span>
                  <span className="now-playing__aya">ayah {atAya.aya_index}</span>
                </>
              : <>Recitation{playing ? ' · playing' : ''}</>}
          </div>
          {/* The line above names an ayah either way, so this one says which of
              the two it is -- being recited now, or waiting to be. */}
          <div className="now-playing__t2">
            {playing ? (atAya ? '' : 'Playing · ') : (atAya ? 'Starts here · ' : 'Not playing · ')}
            {reciterName(settings['quran-sound']) || 'Choose a reciter in Sound'}
          </div>
        </div>
        {playing && <div className="bars"><i /><i /><i /><i /></div>}
      </div>

      {/* The same promise the sheets make, because this button keeps it the same
          way: "quran" is a stored setting, so starting and stopping the
          recitation is saved and then collected by the box -- it is not a
          transport control, however much it looks like one. Without the line
          here, the one place a customer presses something and expects to hear a
          result is the one place nothing explained the wait. */}
      <Note>{SETTLE_NOTE}</Note>

      {/* ------------------------------------------------------------ tiles */}
      <div className="tiles">
        {/* Where the box is and how its times are worked out belong to whoever
            answers for it, not to everyone who can reach the app. */}
        {!isMember && (
          <button className="tile" onClick={() => setSheet('times')}>
            <Icon name="location" size={19} className="tile__icon" />
            <span className="tile__title">Prayer times</span>
            <span className="tile__sub">Location and calculation</span>
          </button>
        )}
        <button className="tile" onClick={() => setSheet('sound')}>
          <Icon name="volume" size={19} className="tile__icon" />
          <span className="tile__title">Sound</span>
          <span className="tile__sub">Azan and recitation</span>
        </button>
        <button className="tile" onClick={() => setSheet('screen')}>
          <Icon name="palette" size={19} className="tile__icon" />
          <span className="tile__title">Screen</span>
          <span className="tile__sub">Background pictures</span>
        </button>
        <button className="tile" onClick={() => setSheet('device')}>
          <Icon name="cog" size={19} className="tile__icon" />
          <span className="tile__title">The box</span>
          <span className="tile__sub">Status, restart, software</span>
        </button>
      </div>

      <div className="footer">
        {preview?.source && (
          <>Times from {preview.source === 'db' ? 'the timetable' : 'the calculator'}</>
        )}
      </div>

      {/* ----------------------------------------------------------- sheets */}

      {sheet === 'times' && (
        <Sheet title="Prayer times"
               subtitle="Set once, when the box arrives. The television follows within a minute."
               onClose={() => setSheet(null)}>
          <SettingLocation DeviceKey={deviceKey} DeviceSetting={settings} onSettingChange={onSettingChange} />
          <SettingPrayerCalc DeviceSetting={settings} onSettingChange={onSettingChange} />
        </Sheet>
      )}

      {sheet === 'sound' && (
        <Sheet title="Sound" subtitle="What the box plays, and when."
               onClose={() => setSheet(null)}>
          <SettingAzan DeviceKey={deviceKey} DeviceSetting={settings} onSettingChange={onSettingChange} />
          <SettingQuran DeviceKey={deviceKey} DeviceSetting={settings} onSettingChange={onSettingChange} />
        </Sheet>
      )}

      {sheet === 'screen' && (
        <Sheet title="Screen" subtitle="How the television looks, and what it looks at."
               onClose={() => setSheet(null)}>
          <SettingDisplayTheme DeviceSetting={settings} onSettingChange={onSettingChange} />

          <div className="section__head"><h2>Pictures</h2><span>Any number; they cycle</span></div>
          <SettingBkImages DeviceSetting={settings} onSettingChange={onSettingChange} />
        </Sheet>
      )}

      {sheet === 'device' && (
        /* A member is sent no commands from here, so the footnote about how long
           one takes to arrive would answer a question they cannot ask. */
        <Sheet title="The box"
               subtitle={isMember
                 ? 'How this one is doing, and who it is shared with.'
                 : 'What this one is called, and how it is doing.'}
               footnote={isMember ? null
                 : 'A box that is awake acts on a command within seconds. '
                 + 'One that has been idle can take up to 15 minutes to notice.'}
               onClose={() => setSheet(null)}>
          {/* Naming the box is the owner's: it is what everyone else in the
              household sees it called. */}
          {!isMember && <DeviceName device={device} user={User} onRenamed={onDevicesChanged} />}
          <DeviceHealth User={User} Device={device} />
          {/* Who else gets a key to the house is the owner's decision. A
              member passing it on is exactly the thing the owner would want to
              have been asked about, so they are not offered it -- and the
              server refuses it from them regardless. */}
          {!isMember && (
            <DeviceShare User={User} device={device} onTransferred={onDevicesChanged} />
          )}
        </Sheet>
      )}
    </div>
  );
};

Home.propTypes = {
  User:             PropTypes.object.isRequired,
  Devices:          PropTypes.array.isRequired,
  Logout:           PropTypes.func.isRequired,
  onAddDevice:      PropTypes.func,
  onDevicesChanged: PropTypes.func,
};

export default Home;
